import React from 'react';
import cn from 'classnames';
import { components as ReactSelectComponents, OptionProps } from 'react-select';
import IconTriangleArrowBottom from '@creditflowresearch/uilib/Icon/IconTriangleArrowBottom';
import IconMinus from '@creditflowresearch/uilib/Icon/IconMinus';
import IconCheck from '@creditflowresearch/uilib/Icon/IconCheck';
import {
  ITreeSelectOption,
  ITreeSelectExtendedOption,
  TTreeSelectOptionClick,
  TGroupedChildrenByParents,
} from './interfaces';

import alignStyles from '@creditflowresearch/uilib/styles/helpers/align.module.scss';
import styles from './treeSelect.module.scss';

interface IProps<SOption extends ITreeSelectOption = ITreeSelectExtendedOption>
  extends OptionProps<SOption> {
  component: React.ComponentType<OptionProps<SOption>>,
  dataTest: string,
  groupedChildrenByParents: TGroupedChildrenByParents,
  selectedTreeParents: SOption['value'][],
  toggleParentOption: (event: React.MouseEvent, value: string) => void,
  optionClickHandler: (event: React.MouseEvent, { data, selectProps }: TTreeSelectOptionClick) => void,
}

export const Option = <SOption extends ITreeSelectExtendedOption>({
  children,
  component: Component,
  dataTest,
  groupedChildrenByParents,
  toggleParentOption,
  selectedTreeParents,
  optionClickHandler,
  ...props
}: IProps<SOption>) => {
  const { data, isSelected, selectProps, options } = props;

  if (data.parent && !selectedTreeParents.includes(data.parent)) {
    return null;
  }

  const { isParent, label, isDisabled } = data;
  const allChildrenValues = groupedChildrenByParents.get(label)?.optionsValues ?? [];
  const selectedValues = (selectProps.value as SOption[]).map(({ value }) => value);
  const isParentWithoutChildren = isParent && allChildrenValues.length === 0;
  const isAllChildrenSelected = data.isParent
    ? allChildrenValues.length > 0 && allChildrenValues.every((child) => selectedValues.includes(child))
    : false;
  const isMinusIconShown = isParent && (options as SOption[]).some(
    ({ value, parent }) => parent === label && selectedValues.includes(value),
  );
  const dataTestAttr = `${ dataTest }_option`;

  return (
    <span data-test={ dataTestAttr }>
      <ReactSelectComponents.Option
        { ...props }
        className={ cn({ [styles.selected]: isSelected }, styles.optionContainer) }
      >
        <div
          className={ cn(alignStyles.flexStart, styles.option) }
          onClick={ (event) => optionClickHandler(event, { data, selectProps }) }
        >
          { isParent && !isParentWithoutChildren && (
            <div
              data-test={ `${ dataTestAttr }_toggle` }
              className={ cn({
                [styles.isOpen]: selectedTreeParents.includes(label),
              }, styles.arrowIcon) }
            >
              <div
                className={ alignStyles.flexCenter }
                onClick={ (event) => toggleParentOption(event, label) }
              >
                <IconTriangleArrowBottom />
              </div>
            </div>
          ) }
          <div className={ cn({
            [styles.isChildOption]: !data.isParent,
            [styles.isParentWithoutChildren]: isParentWithoutChildren,
          }, alignStyles.flex) }
          >
            <div className={ cn({
              [styles.active]: isSelected || isMinusIconShown,
              [styles.disable]: isDisabled,
            }, styles.iconWrapper, alignStyles.flexCenter) }
            >
              { (isSelected || isAllChildrenSelected) && <IconCheck /> }
              { isMinusIconShown && !isAllChildrenSelected && <IconMinus /> }
            </div>
            {
              Component
              ? (
                <Component { ...props }>
                  { children }
                </Component>
              )
              : children
            }
          </div>
        </div>
      </ReactSelectComponents.Option>
    </span>
  );
};

export default Option;
